/**
 * EmployeeSalaries — Monthly employee salary ledger.
 *
 * Mirrors the visual structure of ServicePayments (DashboardLayout + total card +
 * add modal + history list) but for employees instead of services.
 *
 * Backend: /api/finance/employee-salaries/*  (defined in routes/admin_employee_salaries.py)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Plus, Trash2, Calendar, DollarSign, Loader2,
  History, FileText, Eye, X, Briefcase, Upload, Sparkles, CheckCircle2,
  Search, Filter, RefreshCcw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { DirhamPrice } from '../../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLOR = '#10B981';

const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// شهر الراتب = الشهر السابق لتاريخ الإيصال (إيصال بتاريخ 7 يونيو → راتب مايو)
function prevMonthOf(dateStr) {
  const m = /^(\d{4})-(\d{2})/.exec(dateStr || '');
  if (!m) return currentMonthValue();
  let year = parseInt(m[1], 10);
  let month = parseInt(m[2], 10) - 1;
  if (month === 0) { month = 12; year -= 1; }
  return `${year}-${String(month).padStart(2, '0')}`;
}

function formatMonth(v, isAr) {
  if (!v || !/^\d{4}-\d{2}$/.test(v)) return v || '—';
  const [y, m] = v.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${(isAr ? MONTHS_AR : MONTHS_EN)[idx]} ${y}`;
}

const EMPTY_FORM = {
  employee_id: '',
  employee_name: '',
  amount: '',
  month: currentMonthValue(),
  payment_date: new Date().toISOString().split('T')[0],
  notes: '',
  transfer_number: '',
  receipt_file_id: '',
  receipt_filename: '',
};

export default function EmployeeSalaries() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  // iter-237.6 (Feb 2026) — Split entry vs records views via URL param.
  // ?mode=entry → auto-open add modal, hide the ledger table.
  // ?mode=records → show the ledger table only, hide the "add" button.
  // (no param) → legacy behaviour: both visible.
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get('mode') || '').toLowerCase();
  const isEntryMode = mode === 'entry';
  const isRecordsMode = mode === 'records';
  // Parent hub for the smart back button.
  const parentPath = isEntryMode
    ? '/finance/asb606/payments/entry'
    : isRecordsMode
      ? '/finance/asb606/payments/records'
      : '/finance/asb606/payments';
  const parentLabelAr = isEntryMode
    ? 'الإدراج المالي للشركة'
    : isRecordsMode
      ? 'السجل والمتابعة'
      : 'المدفوعات';
  const parentLabelEn = isEntryMode
    ? 'Financial Entry'
    : isRecordsMode
      ? 'Records & Tracking'
      : 'Payments';

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showView, setShowView] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [ocrLoading, setOcrLoading] = useState(false);
  // الإيصال إلزامي: لا حفظ قبل التحقق من إيصال حقيقي عبر الذكاء الاصطناعي
  const [receiptVerified, setReceiptVerified] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch salary history
  const fetchSalaries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/employee-salaries`, { headers });
      setSalaries(res.data?.data || []);
    } catch (e) {
      toast.error(isAr ? 'فشل جلب الرواتب' : 'Failed to load salaries');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch employee list (admins with role admin/staff)
  const fetchEmployees = useCallback(async () => {
    try {
      // Reuse the tasks assignable-admins endpoint — it returns admin/staff users
      const res = await axios.get(`${BACKEND_URL}/api/tasks/assignable-admins`, { headers });
      setEmployees(res.data?.data || []);
    } catch (e) {
      // non-fatal — user can still type manually
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSalaries();
    fetchEmployees();
  }, [fetchSalaries, fetchEmployees]);

  // iter-237.6 — In entry mode, auto-open the add modal so the operator
  // lands directly on the form.
  useEffect(() => {
    if (isEntryMode) {
      setForm(EMPTY_FORM);
      setReceiptVerified(false);
      setShowAdd(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEntryMode]);

  // iter-218o (Feb 2026) — Filters state + memoised filtered list.
  const [filters, setFilters] = useState({
    search: '',
    employee_id: '',
    month: '',        // YYYY-MM (salary month)
    date_from: '',    // payment_date range
    date_to: '',
    min_amount: '',
    max_amount: '',
  });
  const filteredSalaries = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const minA = parseFloat(filters.min_amount) || null;
    const maxA = parseFloat(filters.max_amount) || null;
    return salaries.filter((s) => {
      if (filters.employee_id && s.employee_id !== filters.employee_id) return false;
      if (filters.month && s.month !== filters.month) return false;
      if (filters.date_from && (s.payment_date || '') < filters.date_from) return false;
      if (filters.date_to   && (s.payment_date || '') > filters.date_to)   return false;
      const amt = Number(s.amount) || 0;
      if (minA !== null && amt < minA) return false;
      if (maxA !== null && amt > maxA) return false;
      if (q) {
        const hay = [
          s.employee_name, s.notes, s.reference, s.month,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [salaries, filters]);

  const totalAED = filteredSalaries.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const filtersActive = !!(filters.search || filters.employee_id || filters.month ||
                            filters.date_from || filters.date_to ||
                            filters.min_amount || filters.max_amount);
  const resetFilters = () => setFilters({
    search: '', employee_id: '', month: '', date_from: '', date_to: '',
    min_amount: '', max_amount: '',
  });

  // Submit new salary payment — receipt MUST be uploaded & AI-verified first
  const submitSalary = async () => {
    if (!form.receipt_file_id || !receiptVerified) {
      toast.error(isAr ? 'يجب رفع إيصال تحويل حقيقي والتحقق منه أولاً' : 'A verified transfer receipt is required first');
      return;
    }
    if (!form.employee_id && !form.employee_name.trim()) {
      toast.error(isAr ? 'اختر موظفاً أو أدخل اسماً' : 'Select an employee or enter a name');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error(isAr ? 'أدخل مبلغاً صحيحاً' : 'Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_id: form.employee_id || null,
        employee_name: form.employee_name.trim(),
        amount: Number(form.amount),
        currency: 'AED',
        month: form.month,
        payment_date: form.payment_date,
        notes: form.notes.trim(),
        transfer_number: form.transfer_number.trim() || null,
        receipt_file_id: form.receipt_file_id || null,
      };
      await axios.post(`${BACKEND_URL}/api/finance/employee-salaries`, payload, { headers });
      toast.success(isAr ? 'تمت الإضافة' : 'Saved');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      setReceiptVerified(false);
      if (isEntryMode) {
        // In entry-only mode, redirect back to the parent hub so the operator
        // sees "الإدراج المالي" cards again and can pick the next action.
        navigate(parentPath);
      } else {
        fetchSalaries();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل في الإضافة' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  // Receipt-first flow: AI verifies the image/PDF IS a real receipt, then we store
  // it and auto-fill amount / transfer # / payment date / salary month.
  const handleReceiptUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(isAr ? 'حجم الملف أكبر من 8 ميجا' : 'File larger than 8 MB');
      return;
    }
    const isImage = file.type.startsWith('image/');
    const isPdf   = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      toast.error(isAr ? 'يُرجى رفع صورة (PNG / JPG / WEBP) أو PDF' : 'Please upload an image (PNG / JPG / WEBP) or a PDF');
      return;
    }
    setOcrLoading(true);
    setReceiptVerified(false);
    try {
      const uploadHeaders = { Authorization: `Bearer ${token}` };
      const buildFD = () => {
        const fd = new FormData();
        fd.append('file', file);
        return fd;
      };

      // 1) AI verification FIRST — reject anything that is not a real receipt
      const ocrRes = await axios.post(`${BACKEND_URL}/api/ocr/payment-receipt`, buildFD(), { headers: uploadHeaders });

      if (ocrRes.data?.success === false || ocrRes.data?.error === 'NOT_A_RECEIPT') {
        setForm((prev) => ({ ...prev, receipt_file_id: '', receipt_filename: '' }));
        toast.error(
          ocrRes.data?.message ||
          (isAr ? '❌ الصورة ليست إيصال دفع حقيقي — ارفع إيصالاً صحيحاً' : '❌ Image is not a real payment receipt — upload a valid one'),
          { duration: 8000 },
        );
        return;
      }

      // 2) Receipt is genuine → store it
      const storeRes = await axios.post(`${BACKEND_URL}/api/upload/file`, buildFD(), { headers: uploadHeaders });
      const fileId = storeRes.data?.file_id || '';
      const fileName = storeRes.data?.filename || file.name;

      // 3) Auto-fill: amount + transfer # + payment date (= receipt date)
      //    + salary month (= month BEFORE the receipt date)
      const data = ocrRes.data?.data || {};
      const receiptDate = data.payment_date || form.payment_date;
      setForm((prev) => ({
        ...prev,
        receipt_file_id: fileId,
        receipt_filename: fileName,
        payment_date: receiptDate,
        month: prevMonthOf(receiptDate),
        amount: data.amount != null ? String(data.amount) : prev.amount,
        transfer_number: data.transfer_number || prev.transfer_number,
      }));
      setReceiptVerified(true);
      toast.success(isAr ? '✨ إيصال موثّق — تم تعبئة البيانات تلقائياً' : '✨ Receipt verified — data auto-filled');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      toast.error(
        msg ||
        (isAr ? 'فشل تحليل الإيصال' : 'Failed to analyse receipt')
      );
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteSalary = async (id) => {
    if (!window.confirm(isAr ? 'هل أنت متأكد من الحذف؟' : 'Delete this entry?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/finance/employee-salaries/${id}`, { headers });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      fetchSalaries();
    } catch (e) {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    }
  };

  return (
    <DashboardLayout
      title="رواتب الموظفين"
      titleEn="Employee Salaries"
      subtitle={isEntryMode ? 'إدراج راتب موظف جديد' : (isRecordsMode ? 'سجل رواتب الموظفين' : 'إدارة وسجل رواتب الموظفين الشهرية')}
      subtitleEn={isEntryMode ? 'Add a new employee salary payment' : (isRecordsMode ? 'Employee salary records' : 'Monthly employee salary management')}
      screenCode="ASB-FIN-PAY-SAL"
      backPath={parentPath}
      onBack={() => navigate(parentPath)}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: parentLabelAr, labelEn: parentLabelEn, path: parentPath },
        { labelAr: 'رواتب الموظفين', labelEn: 'Employee Salaries' },
      ]}
    >
      <main className="relative z-10 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="employee-salaries-page">

          {/* Total + Add button — hidden in entry mode (the form IS the page there). */}
          {!isEntryMode && (
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            <div
              className="flex-1 rounded-xl p-5 border"
              style={{
                background: 'linear-gradient(to right, rgba(16,185,129,0.18), rgba(16,185,129,0.05))',
                borderColor: 'rgba(16,185,129,0.35)',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm mb-1" style={{ color: COLOR }}>
                    {isAr ? 'إجمالي الرواتب' : 'Total Salaries'}
                  </p>
                  <p className="text-white text-2xl font-bold">
                    {totalAED.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    <span className="text-base text-emerald-400 ms-1">{isAr ? 'د.إ' : 'AED'}</span>
                  </p>
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(16,185,129,0.20)' }}
                >
                  <DollarSign className="h-6 w-6 text-emerald-400" />
                </div>
              </div>
            </div>

            <Button
              onClick={() => { setForm(EMPTY_FORM); setReceiptVerified(false); setShowAdd(true); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 h-14 rounded-xl"
              data-testid="btn-add-salary"
              style={{ display: isRecordsMode ? 'none' : undefined }}
            >
              <Plus className="h-5 w-5 me-2" />
              {isAr ? 'إضافة دفعة راتب' : 'Add Salary Payment'}
            </Button>
          </div>
          )}

          {/* iter-218o (Feb 2026) — Filters row */}
          {/* iter-237.6 — Hide filters + history table when entry-only mode. */}
          {!isEntryMode && (
          <div className="mt-4 rounded-2xl bg-white/[0.02] border border-white/10 p-4" data-testid="salaries-filters">
            <div className="flex items-center gap-2 mb-3 text-white/60 text-xs">
              <Filter className="w-4 h-4" />
              <span className="font-medium">{isAr ? 'الفلاتر' : 'Filters'}</span>
              {filtersActive && (
                <button onClick={resetFilters}
                        className="ms-auto inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 text-[11px]"
                        data-testid="salaries-filters-reset">
                  <RefreshCcw className="w-3 h-3" />
                  {isAr ? 'إعادة تعيين' : 'Reset'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              {/* Search */}
              <div className="relative md:col-span-2">
                <Search className="absolute inset-y-0 start-4 my-auto w-4 h-4 text-white/40 pointer-events-none" />
                <input
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder={isAr ? 'بحث بالاسم / الملاحظات…' : 'Search by name / notes…'}
                  className="w-full bg-[#050505] border border-white/10 rounded-full h-10 ps-10 pe-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                  data-testid="salaries-filter-search"
                />
              </div>
              {/* Employee filter */}
              <select
                value={filters.employee_id}
                onChange={(e) => setFilters(f => ({ ...f, employee_id: e.target.value }))}
                className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                data-testid="salaries-filter-employee"
              >
                <option value="">{isAr ? 'كل الموظفين' : 'All employees'}</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id}>{e.name || e.username || e.email}</option>
                ))}
              </select>
              {/* Salary month (YYYY-MM) */}
              <input type="month" value={filters.month}
                     onChange={(e) => setFilters(f => ({ ...f, month: e.target.value }))}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="salaries-filter-month" dir="ltr" />
              {/* Payment date range */}
              <input type="date" value={filters.date_from}
                     onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                     placeholder={isAr ? 'من تاريخ' : 'From'}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="salaries-filter-date-from" dir="ltr" />
              <input type="date" value={filters.date_to}
                     onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                     placeholder={isAr ? 'إلى تاريخ' : 'To'}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="salaries-filter-date-to" dir="ltr" />
              {/* Amount range */}
              <input type="number" min="0" value={filters.min_amount}
                     onChange={(e) => setFilters(f => ({ ...f, min_amount: e.target.value }))}
                     placeholder={isAr ? 'من مبلغ' : 'Min amount'}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="salaries-filter-min-amount" dir="ltr" />
              <input type="number" min="0" value={filters.max_amount}
                     onChange={(e) => setFilters(f => ({ ...f, max_amount: e.target.value }))}
                     placeholder={isAr ? 'إلى مبلغ' : 'Max amount'}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="salaries-filter-max-amount" dir="ltr" />
            </div>
            <div className="mt-2 text-[11px] text-white/50">
              {isAr
                ? `النتائج: ${filteredSalaries.length} من ${salaries.length} راتب${filtersActive ? ' — الإجمالي أعلاه بعد الفلترة' : ''}`
                : `Showing ${filteredSalaries.length} of ${salaries.length} salaries${filtersActive ? ' — total above reflects the filter' : ''}`}
            </div>
          </div>
          )}

          {/* List — hidden in entry-only mode (iter-237.6) */}
          {!isEntryMode && (
          <div className="mt-6 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-400" />
              {isAr ? 'سجل الرواتب' : 'Salary History'}
              <span className="text-sm text-[#808080] font-normal ms-2">
                ({filteredSalaries.length}{filtersActive ? ` / ${salaries.length}` : ''})
              </span>
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              </div>
            ) : filteredSalaries.length === 0 ? (
              <div className="text-center py-12 bg-[#0a0a0a] rounded-xl border border-white/5">
                <Users className="h-12 w-12 mx-auto mb-3 text-emerald-500/30" />
                <p className="text-[#808080]">
                  {filtersActive
                    ? (isAr ? 'لا نتائج تطابق الفلاتر الحالية' : 'No results match the current filters')
                    : (isAr ? 'لا توجد رواتب مسجّلة بعد' : 'No salaries recorded yet')}
                </p>
                <p className="text-sm text-[#606060] mt-1">
                  {filtersActive
                    ? (isAr ? 'جرّب إعادة تعيين الفلاتر' : 'Try resetting the filters')
                    : (isAr ? 'اضغط على "إضافة راتب" للبدء' : 'Click "Add Salary" to begin')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSalaries.map((s, idx) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx * 0.03, 0.4) }}
                    className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all"
                    data-testid={`salary-row-${s.id}`}
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                        <Briefcase className="h-6 w-6 text-emerald-400" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-white font-medium truncate">{s.employee_name || '—'}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            {formatMonth(s.month, isAr)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#606060] flex-wrap">
                          {s.payment_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(s.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          {s.notes && (
                            <span className="truncate max-w-xs" title={s.notes}>
                              <FileText className="h-3 w-3 inline me-1" />
                              {s.notes}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-end shrink-0">
                        <p className="text-white font-bold text-lg">
                          {Number(s.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          <span className="text-sm text-emerald-400 ms-1">{isAr ? 'د.إ' : 'AED'}</span>
                        </p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowView(s)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`view-salary-${s.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSalary(s.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-salary-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </main>

      {/* Add form — inline card in entry mode, floating modal otherwise. */}
      {showAdd && (
        <div className={isEntryMode
          ? "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
          : "fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto"}>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className={isEntryMode
              ? "bg-[#1a1a2e] rounded-2xl border border-white/10 w-full"
              : "bg-[#1a1a2e] rounded-2xl border border-white/10 w-full max-w-lg max-h-[calc(100vh-7rem)] overflow-y-auto"}
            data-testid="add-salary-modal"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-emerald-500" />
                  <h2 className="text-xl font-bold text-white">
                    {isEntryMode
                      ? (isAr ? 'إدراج راتب موظف' : 'Salary Entry')
                      : (isAr ? 'إضافة دفعة راتب' : 'Add Salary Payment')}
                  </h2>
                </div>
                {/* Close button hidden in entry mode — form IS the page there. */}
                {!isEntryMode && (
                  <button
                    onClick={() => setShowAdd(false)}
                    className="text-[#808080] hover:text-white"
                    data-testid="close-add-salary"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {/* الخطوة 1 (إلزامية): رفع إيصال حقيقي والتحقق منه */}
                <div className={`rounded-xl border p-3 ${receiptVerified
                  ? 'border-emerald-500/40 bg-gradient-to-r from-emerald-500/5 to-emerald-500/10'
                  : 'border-red-500/30 bg-gradient-to-r from-red-500/5 to-amber-500/10'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className={`h-4 w-4 ${receiptVerified ? 'text-emerald-400' : 'text-amber-400'}`} />
                    <p className={`text-xs font-semibold ${receiptVerified ? 'text-emerald-200' : 'text-amber-200'}`}>
                      {receiptVerified
                        ? (isAr ? '✓ إيصال موثّق — البيانات معبأة تلقائياً' : '✓ Receipt verified — data auto-filled')
                        : (isAr ? 'الخطوة 1 (إلزامية): ارفع إيصال التحويل' : 'Step 1 (required): upload the transfer receipt')}
                    </p>
                  </div>
                  <p className="text-[10px] text-white/50 mb-2 leading-relaxed">
                    {isAr
                      ? 'الذكاء الاصطناعي يتحقق أن الملف (صورة أو PDF) إيصال حقيقي، ثم يعبّئ تلقائياً: المبلغ، رقم الحوالة، تاريخ الدفع (تاريخ الإيصال) وشهر الراتب (الشهر السابق). أنت فقط تختار الموظف وتضيف الملاحظات.'
                      : 'AI verifies the file (image or PDF) is a genuine receipt, then auto-fills: amount, transfer #, payment date (receipt date) and salary month (previous month). You only pick the employee and add notes.'}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                    onChange={(e) => handleReceiptUpload(e.target.files?.[0])}
                    className="hidden"
                    data-testid="salary-receipt-file-input"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={ocrLoading}
                      className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 rounded-lg h-9 px-3"
                      data-testid="salary-receipt-upload-btn"
                    >
                      {ocrLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin me-2" />
                          {isAr ? 'جاري التحقق من الإيصال…' : 'Verifying receipt…'}
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 me-2" />
                          {receiptVerified
                            ? (isAr ? 'استبدال الإيصال' : 'Replace Receipt')
                            : (isAr ? 'رفع إيصال (صورة)' : 'Upload Receipt (Image)')}
                        </>
                      )}
                    </Button>
                    {form.receipt_filename && receiptVerified && !ocrLoading && (
                      <span className="flex items-center gap-1 text-[11px] text-emerald-300" data-testid="salary-receipt-uploaded">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {form.receipt_filename}
                      </span>
                    )}
                  </div>
                </div>

                {/* الخطوة 2: باقي الحقول — تُفعَّل بعد التحقق من الإيصال */}
                <div className={receiptVerified ? 'space-y-4' : 'space-y-4 opacity-40 pointer-events-none select-none'} data-testid="salary-form-fields">

                {/* Employee */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {isAr ? 'الموظف *' : 'Employee *'}
                  </label>
                  <Select
                    value={form.employee_id || '__manual__'}
                    onValueChange={(v) => {
                      if (v === '__manual__') {
                        setForm({ ...form, employee_id: '', employee_name: '' });
                      } else {
                        const e = employees.find((x) => x.id === v);
                        setForm({ ...form, employee_id: v, employee_name: e?.name || '' });
                      }
                    }}
                  >
                    <SelectTrigger
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                      data-testid="salary-employee-select"
                    >
                      <SelectValue placeholder={isAr ? 'اختر موظفاً' : 'Select employee'} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                      <SelectItem value="__manual__">
                        {isAr ? '(إدخال يدوي للاسم)' : '(Manual entry)'}
                      </SelectItem>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} <span className="text-[10px] text-[#71717A]" dir="ltr">({e.email})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Manual employee name (shown when no employee picked) */}
                {!form.employee_id && (
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {isAr ? 'اسم الموظف *' : 'Employee Name *'}
                    </label>
                    <Input
                      value={form.employee_name}
                      onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                      placeholder={isAr ? 'مثال: محمد علي' : 'e.g., Mohamed Ali'}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                      data-testid="salary-employee-name"
                    />
                  </div>
                )}

                {/* Month + payment date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {isAr ? 'شهر الراتب *' : 'Salary Month *'}
                    </label>
                    <Input
                      type="month"
                      value={form.month}
                      onChange={(e) => setForm({ ...form, month: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                      dir="ltr"
                      data-testid="salary-month"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      {isAr ? 'تاريخ الدفع *' : 'Payment Date *'}
                    </label>
                    <Input
                      type="date"
                      value={form.payment_date}
                      onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                      dir="ltr"
                      data-testid="salary-payment-date"
                    />
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {isAr ? 'المبلغ *' : 'Amount *'}
                  </label>
                  <Input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                    dir="ltr"
                    data-testid="salary-amount"
                  />
                </div>

                {/* Transfer number */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {isAr ? 'رقم التحويل' : 'Transfer Number'}
                  </label>
                  <Input
                    value={form.transfer_number}
                    onChange={(e) => setForm({ ...form, transfer_number: e.target.value })}
                    placeholder={isAr ? 'مثال: FT240120ABCDEF' : 'e.g., FT240120ABCDEF'}
                    className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                    dir="ltr"
                    data-testid="salary-transfer-number"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {isAr ? 'ملاحظات' : 'Notes'}
                  </label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    placeholder={isAr ? 'مثال: راتب أساسي + بدل سكن' : 'e.g., Base salary + housing'}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    data-testid="salary-notes"
                  />
                </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (isEntryMode) {
                        // In entry mode, "cancel" means leave the entry hub — no local close.
                        navigate(parentPath);
                      } else {
                        setShowAdd(false);
                      }
                    }}
                    className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-xl h-12"
                  >
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button
                    onClick={submitSalary}
                    disabled={saving || !receiptVerified}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 disabled:opacity-50"
                    data-testid="submit-salary"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Plus className="h-4 w-4 me-2" />}
                    {receiptVerified
                      ? (isAr ? 'حفظ الدفعة' : 'Save Payment')
                      : (isAr ? 'ارفع الإيصال أولاً' : 'Upload receipt first')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* View dialog */}
      {showView && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowView(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1a1a2e] rounded-2xl border border-white/10 w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="view-salary-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-400" />
                {isAr ? 'تفاصيل الراتب' : 'Salary Details'}
              </h3>
              <button onClick={() => setShowView(null)} className="text-[#808080] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <Row label={isAr ? 'الموظف' : 'Employee'} value={showView.employee_name} />
              <Row label={isAr ? 'الشهر' : 'Month'} value={formatMonth(showView.month, isAr)} />
              <Row
                label={isAr ? 'المبلغ' : 'Amount'}
                value={Number(showView.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                strong
              />
              <Row
                label={isAr ? 'تاريخ الدفع' : 'Payment date'}
                value={showView.payment_date ? new Date(showView.payment_date).toLocaleDateString('en-GB') : '—'}
              />
              {showView.transfer_number && (
                <Row label={isAr ? 'رقم التحويل' : 'Transfer #'} value={showView.transfer_number} />
              )}
              {showView.receipt_file_id && (
                <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5">
                  <span className="text-[#A1A1AA]">{isAr ? 'إيصال التحويل' : 'Receipt'}</span>
                  <a
                    href={`${BACKEND_URL}/api/upload/file/${showView.receipt_file_id}?token=${encodeURIComponent(token || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline text-sm"
                    data-testid={`view-salary-receipt-${showView.id}`}
                  >
                    {isAr ? 'عرض الإيصال' : 'View receipt'}
                  </a>
                </div>
              )}
              {showView.notes && <Row label={isAr ? 'ملاحظات' : 'Notes'} value={showView.notes} />}
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <span className="text-[#A1A1AA]">{label}</span>
      <span className={`text-white ${strong ? 'font-bold' : ''}`}>{value || '—'}</span>
    </div>
  );
}
